import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatCard } from "../components/shared";
import { formatMoney, formatNum } from "../lib/constants";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Factory, TrendingUp } from "lucide-react";

function ProductionSummary({ start, end }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/production-summary", { params: { start, end } }).then((r) => setData(r.data)).catch(() => {});
  }, [start, end]);
  if (!data) return <div className="font-mono text-sm text-muted-foreground py-8">Loading…</div>;
  const cur = data.base_currency;
  return (
    <div data-testid="production-summary">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatCard label="Units Produced" value={formatNum(data.total_qty, 0)} icon={Factory} index={0} />
        <StatCard label="Cost of Goods Produced" value={formatMoney(data.total_cost, cur)} accent index={1} />
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-secondary">
            <TableHead>Product</TableHead><TableHead className="text-right">Runs</TableHead><TableHead className="text-right">Units</TableHead>
            <TableHead className="text-right">Avg Unit Cost</TableHead><TableHead className="text-right">Total Cost</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No production in this period.</TableCell></TableRow>}
            {data.rows.map((r) => (
              <TableRow key={r.product_id} data-testid="production-summary-row">
                <TableCell className="font-medium">{r.product_name}</TableCell>
                <TableCell className="text-right font-mono">{formatNum(r.runs, 0)}</TableCell>
                <TableCell className="text-right font-mono">{formatNum(r.quantity, 0)}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{formatMoney(r.avg_unit_cost, cur)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{formatMoney(r.total_cost, cur)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SalesSummary({ start, end }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/sales-summary", { params: { start, end } }).then((r) => setData(r.data)).catch(() => {});
  }, [start, end]);
  if (!data) return <div className="font-mono text-sm text-muted-foreground py-8">Loading…</div>;
  const cur = data.base_currency;
  return (
    <div data-testid="sales-summary">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatCard label="Units Sold" value={formatNum(data.total_qty, 0)} icon={TrendingUp} index={0} />
        <StatCard label="Total Sales" value={formatMoney(data.total_amount, cur)} accent index={1} />
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-secondary">
            <TableHead>Product</TableHead><TableHead className="text-right">Units Sold</TableHead>
            <TableHead className="text-right">Avg Price</TableHead><TableHead className="text-right">Total Amount</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8">No sales in this period.</TableCell></TableRow>}
            {data.rows.map((r) => (
              <TableRow key={r.product_id} data-testid="sales-summary-row">
                <TableCell className="font-medium">{r.product_name}</TableCell>
                <TableCell className="text-right font-mono">{formatNum(r.quantity, 0)}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{formatMoney(r.avg_price, cur)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{formatMoney(r.amount, cur)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const showProduction = user.role === "admin" || user.role === "inventory_manager" || user.role === "accountant";
  const showSales = user.role === "admin" || user.role === "accountant" || user.role === "sales";
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const defaultTab = showProduction ? "production" : "sales";

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Periodic production costing & sales summaries" />

      <div className="flex flex-wrap items-end gap-3 mb-6 border border-border bg-card p-4">
        <div><Label>From</Label><Input type="date" className="rounded-none mt-1" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start-input" /></div>
        <div><Label>To</Label><Input type="date" className="rounded-none mt-1" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-input" /></div>
        <p className="text-xs text-muted-foreground pb-2">Leave blank for all-time.</p>
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="rounded-none">
          {showProduction && <TabsTrigger value="production" className="rounded-none" data-testid="tab-production-summary">Production Costing</TabsTrigger>}
          {showSales && <TabsTrigger value="sales" className="rounded-none" data-testid="tab-sales-summary">Sales Summary</TabsTrigger>}
        </TabsList>
        {showProduction && <TabsContent value="production" className="mt-6"><ProductionSummary start={start} end={end} /></TabsContent>}
        {showSales && <TabsContent value="sales" className="mt-6"><SalesSummary start={start} end={end} /></TabsContent>}
      </Tabs>
    </div>
  );
}
